import type { ColumnDef } from '@tanstack/react-table';
import { MoreHorizontal, PencilLine, Trash2 } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ChangelogChangelogReadModelTag,
  ChangelogChangelogReadModelTarget,
  type ChangelogChangelogReadModel
} from '@r/api/generated/client.schemas.ts';
import { DataTableColumnHeader } from '@r/components/data-table/data-table-column-header.tsx';
import type { DataTableFilterField } from '@r/types/index.ts';
import { cn } from '@r/utils/utils.ts';
import { Badge } from '@ui/badge.tsx';
import { Button } from '@ui/button.tsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@ui/dropdown-menu.tsx';

import { CHANGELOG_TAG_BADGE_CLASS } from '@r/components/changelogs/changelog-tag.ts';

import { ChangelogMediaCell } from './changelog-media-cell.tsx';

/**
 * Champs de filtrage de la table de gestion des changelogs, affichés dans la toolbar
 * avancée : recherche texte sur le titre, filtres à facettes sur catégorie/cible,
 * plage de dates sur la date de création.
 */
export function useChangelogsFilterFields(): DataTableFilterField<ChangelogChangelogReadModel>[] {
  const { t } = useTranslation();

  return [
    {
      label: t('fiddy.admin.changelog.column.title'),
      value: 'title',
      placeholder: t('fiddy.admin.changelog.column.title')
    },
    {
      label: t('fiddy.admin.changelog.column.tag'),
      value: 'tag',
      options: Object.values(ChangelogChangelogReadModelTag).map((tag) => ({
        label: t(`changelog.tag.${tag}`),
        value: tag
      }))
    },
    {
      label: t('fiddy.admin.changelog.column.target'),
      value: 'target',
      options: Object.values(ChangelogChangelogReadModelTarget).map((target) => ({
        label: t(`fiddy.admin.changelog.target.${target}`),
        value: target
      }))
    },
    {
      label: t('fiddy.admin.changelog.column.created.at'),
      value: 'createdAt',
      placeholder: t('fiddy.admin.changelog.column.created.at'),
      isDateRange: true
    }
  ];
}

/**
 * Colonnes de la table de gestion des changelogs : titre, tag, cible, date et actions.
 */
export function useChangelogsColumns(
  onEdit: (changelog: ChangelogChangelogReadModel) => void,
  onDelete: (changelog: ChangelogChangelogReadModel) => void
): ColumnDef<ChangelogChangelogReadModel>[] {
  const { t, i18n } = useTranslation();
  // Coûteux à construire (tables de données locale) : une seule instance par langue,
  // pas une par ligne rendue.
  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'short', year: 'numeric' }),
    [i18n.language]
  );

  return [
    {
      accessorKey: 'title',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('fiddy.admin.changelog.column.title')} />
      ),
      cell: ({ row }) => <span className="font-medium">{row.original.title}</span>
    },
    {
      accessorKey: 'tag',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('fiddy.admin.changelog.column.tag')} />
      ),
      cell: ({ row }) => (
        <Badge className={cn(CHANGELOG_TAG_BADGE_CLASS[row.original.tag], 'font-normal')}>
          {t(`changelog.tag.${row.original.tag}`)}
        </Badge>
      )
    },
    {
      accessorKey: 'target',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('fiddy.admin.changelog.column.target')} />
      ),
      cell: ({ row }) => (
        <Badge variant="outline" className="font-normal">
          {t(`fiddy.admin.changelog.target.${row.original.target}`)}
        </Badge>
      )
    },
    {
      accessorKey: 'createdAt',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('fiddy.admin.changelog.column.created.at')}
        />
      ),
      cell: ({ row }) =>
        row.original.createdAt ? dateFormatter.format(new Date(row.original.createdAt)) : ''
    },
    {
      id: 'media',
      header: t('fiddy.admin.changelog.column.media'),
      cell: ({ row }) => <ChangelogMediaCell pathUri={row.original.media?.pathUri} />
    },
    {
      id: 'actions',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="flex h-8 w-8 p-0 data-[state=open]:bg-muted float-end"
              aria-label={t('fiddy.admin.changelog.actions.label', { title: row.original.title })}
            >
              <MoreHorizontal className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onEdit(row.original)}>
              <PencilLine className="mr-2 size-4" />
              {t('fiddy.admin.changelog.actions.edit')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => onDelete(row.original)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 size-4" />
              {t('fiddy.admin.changelog.actions.delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )
    }
  ];
}
